// PoolScreener orchestrator — turns the DefiLlama universe into a ranked list
// of +EV candidate pools. For each pool it gathers detail (subgraph/GeckoTerminal)
// + the volatile leg's Binance history (closes, volumes, funding), runs the six
// core screens, and scores the expected annual edge. The pure `runScreens`
// carries all the logic; the async `screenPools` only does I/O + ranking.
import {
  exitsPerYear,
  expectedDaysInRange,
  lvrAnnual,
  realizedVolDaily,
  screen1LvrBreakeven,
  screen2FlowToxicity,
  screen3IvRv,
  screen4Funding,
  screen5LiqHeadroom,
  screen6Emissions,
  type ScreenResult,
} from './core/index';
import {
  fetchBinanceSymbols,
  fetchDailyKlines,
  fetchFundingRates,
  fetchGeckoDailyVolume,
  fetchGeckoPoolDetail,
  fetchPoolUniverse,
  fetchSubgraphFeeTier,
  gtNetwork,
  isCexListed,
  type PoolUniverseItem,
  type UniverseOpts,
} from './sources/index';

// Look-back windows — a full quarter so the risk + yield inputs are stable and
// a single noisy day can't swing the numbers.
const HISTORY_DAYS = 90;
const FUNDING_INTERVALS = 90 * 3; // 8h funding → 3/day

// Wrapped-token → CEX base symbol (Binance lists the unwrapped asset).
const CEX_ALIAS: Record<string, string> = {
  WETH: 'ETH',
  WBTC: 'BTC',
  WSTETH: 'ETH',
  WEETH: 'ETH',
  CBETH: 'ETH',
  CBBTC: 'BTC',
  LBTC: 'BTC',
};

// Stablecoins — never the volatile/hedgeable leg.
const STABLES: ReadonlySet<string> = new Set([
  'USDC',
  'USDT',
  'DAI',
  'USDBC',
  'USDE',
  'USDS',
  'FRAX',
  'GHO',
  'LUSD',
  'CRVUSD',
  'EURC',
]);

/** Pure: the volatile leg's Binance base symbol. DefiLlama lists token0 first,
 *  which is often the STABLE (e.g. "USDC-CBBTC" — the volatile leg is CBBTC), so
 *  pick the non-stable token regardless of order rather than assuming BASE-QUOTE.
 *  Using the stable side would compute ~0 vol and hide all churn/LVR. */
export function baseAssetForCex(symbol: string): string {
  const parts = symbol
    .split('-')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  const volatile = parts.find((p) => !STABLES.has(p)) ?? parts[0] ?? '';
  return CEX_ALIAS[volatile] ?? volatile;
}

/** Pure: an effective fee tier derived from DefiLlama's realized fee APR, used
 *  only when neither the subgraph nor GeckoTerminal reports the nominal tier.
 *  effTier = (apyBase/365) · TVL / volume, so Screen 1 then tests realized fee
 *  APR vs LVR (a valid economic check) rather than the nominal tier. */
export function deriveFeeTier(pool: PoolUniverseItem): number | null {
  if (pool.apyBase === null || pool.volumeUsd1d === null) return null;
  if (pool.volumeUsd1d <= 0 || pool.tvlUsd <= 0) return null;
  const dailyFeeReturn = pool.apyBase / 100 / 365;
  return (dailyFeeReturn * pool.tvlUsd) / pool.volumeUsd1d;
}

export interface PoolDetail {
  poolAddress: string | null;
  feeTier: number | null;
  /** Real on-chain TVL from GeckoTerminal reserves — authoritative. Falls back
   *  to DefiLlama's (unreliable) TVL only when GeckoTerminal can't resolve. */
  tvlUsd: number;
  /** Daily USD volume — a 90d average of GeckoTerminal OHLCV (smooths the noisy
   *  single day); falls back to GeckoTerminal 24h, then DefiLlama. */
  dailyVolumeUsd: number;
  midPrice: number | null;
  source: 'geckoterminal' | 'gecko+subgraph' | 'defillama' | 'none';
}

export interface ScreenInputs {
  pool: PoolUniverseItem;
  detail: PoolDetail;
  base: string;
  cexListed: boolean;
  closes: number[]; // 30d volatile-leg daily closes (realized vol + mid)
  quoteVolumes: number[]; // 30d daily quote volumes (flow toxicity)
  funding: number[]; // per-interval funding fractions
  aeroPrices: number[]; // 30d AERO closes (emissions decay warning)
  rangeWidth: number; // strategy range half-width, e.g. 0.10
  leverage: number; // hedge leverage, e.g. 2
  redeployCost: number; // execution friction per redeploy, fraction of position
}

export interface ScreenedPool {
  pool: PoolUniverseItem;
  detail: PoolDetail;
  base: string;
  cexListed: boolean;
  /** Effective fee tier actually fed to the screens (nominal or derived). */
  feeTier: number;
  feeTierDerived: boolean;
  sigmaDaily: number;
  screens: {
    lvrBreakeven: ScreenResult;
    flowToxicity: ScreenResult;
    ivRv: ScreenResult;
    funding: ScreenResult;
    liqHeadroom: ScreenResult;
    emissions: ScreenResult | null; // Aerodrome only
  };
  /** Count of PASS verdicts across the applicable screens. */
  passCount: number;
  /** Durable edge: real fees + funding − LVR (survives emissions decay). Null
   *  without vol data. */
  feeEdgeApr: number | null;
  /** Volatile bonus: Aerodrome gauge emissions APR (0 for Uniswap). */
  emissionsEdgeApr: number;
  /** Total annualized expected edge (feeEdgeApr + emissionsEdgeApr); null
   *  without vol data. */
  expectedEdgeAnnual: number | null;
  // ── churn / real-APR ───────────────────────────────────────────────
  /** Expected range exits (≈ redeploys) per year at the pool's vol + range. */
  exitsPerYear: number;
  /** Expected days in range per epoch (Infinity when vol data is missing). */
  daysInRange: number;
  /** Yield accrued in one in-range epoch, as a fraction of position value. */
  feesPerEpoch: number | null;
  /** Execution friction lost to redeploys per year (exits/yr × cost). */
  churnDragApr: number;
  /** Real APR after churn: expectedEdgeAnnual − churnDragApr. Null without vol.
   *  This is the default sort key. */
  netApr: number | null;
}

/** Pure: run all six screens + the composite score for one pool. */
export function runScreens(i: ScreenInputs): ScreenedPool {
  const isAero = i.pool.project === 'aerodrome-slipstream';
  // Real on-chain economics (GeckoTerminal); the same TVL base feeds every
  // screen so they stay consistent. DefiLlama's TVL/volume are unreliable and
  // are only the last-resort fallback (baked into detail by resolveDetail).
  const tvl = i.detail.tvlUsd;
  const dailyVolume = i.detail.dailyVolumeUsd;
  const sigmaDaily = realizedVolDaily(i.closes);
  const lastClose = i.closes[i.closes.length - 1];
  const midPrice = i.detail.midPrice ?? (lastClose !== undefined ? lastClose : 0);

  const nominalFee = i.detail.feeTier;
  const derived = nominalFee === null || nominalFee <= 0;
  const feeTier = derived ? (deriveFeeTier(i.pool) ?? 0) : nominalFee;
  const tickLiquidityUsd = tvl;

  const lvrBreakeven = screen1LvrBreakeven({ dailyVolume, feeTier, tvl, sigmaDaily });
  const flowToxicity = screen2FlowToxicity({
    cexListed: i.cexListed,
    dailyVolumes: i.quoteVolumes,
  });
  const ivRv = screen3IvRv({ feeTier, dailyVolume, tickLiquidityUsd, dailyCloses: i.closes });
  const funding = screen4Funding({ fundingRates: i.funding });
  const liqHeadroom = screen5LiqHeadroom({
    midPrice,
    rangeWidth: i.rangeWidth,
    leverage: i.leverage,
  });
  const emissions = isAero
    ? screen6Emissions({
        emissionsApr: (i.pool.apyReward ?? 0) / 100,
        dailyVolume,
        feeTier,
        tvl,
        sigmaDaily,
        aeroPrices: i.aeroPrices,
      })
    : null;

  const applicable: ScreenResult[] = [
    lvrBreakeven,
    flowToxicity,
    ivRv,
    funding,
    liqHeadroom,
    ...(emissions ? [emissions] : []),
  ];
  const passCount = applicable.filter((s) => s.verdict === 'PASS').length;

  // Edge, split into a DURABLE leg and a VOLATILE emissions leg so an emissions
  // farm doesn't masquerade as durable fee yield:
  //   feeEdgeApr       = real swap fees + funding − LVR (what survives once
  //                      emissions decay). Uniswap earns fees; STAKED Aerodrome
  //                      forfeits fees for emissions, so its fee leg is 0.
  //   emissionsEdgeApr = Aerodrome gauge emissions (DefiLlama apyReward) — high
  //                      but decaying and size-sensitive; the only source for it.
  // Fees use REAL numbers (365·feeTier·volume/TVL), not DefiLlama's apyBase.
  const feeAprReal = tvl > 0 ? (365 * feeTier * dailyVolume) / tvl : 0;
  const feeApr = isAero ? 0 : feeAprReal;
  const emissionsEdgeApr = isAero ? (i.pool.apyReward ?? 0) / 100 : 0;
  const fundingCarryApr = funding.verdict === 'INSUFFICIENT_DATA' ? 0 : funding.value;
  const feeEdgeApr =
    sigmaDaily > 0 ? feeApr + fundingCarryApr - lvrAnnual(sigmaDaily) : null;
  const edge = feeEdgeApr === null ? null : feeEdgeApr + emissionsEdgeApr;

  // Churn / real APR. A ±rangeWidth position exits its range ~exits/yr times
  // (driven by vol), and each exit forces a redeploy that costs i.redeployCost
  // (gas + rebalance slippage + hedge round-trip) — friction the continuous LVR
  // doesn't capture. netApr subtracts that per-year drag from the edge; a
  // high-vol pool with a great gross edge can still net poorly.
  const exits = exitsPerYear(sigmaDaily, i.rangeWidth);
  const daysInRange = expectedDaysInRange(sigmaDaily, i.rangeWidth);
  const grossYieldApr = feeApr + emissionsEdgeApr; // LP yield accrued in range
  const feesPerEpoch = Number.isFinite(daysInRange)
    ? (grossYieldApr / 365) * daysInRange
    : null;
  const churnDragApr = exits * i.redeployCost;
  const netApr = edge === null ? null : edge - churnDragApr;

  return {
    pool: i.pool,
    detail: i.detail,
    base: i.base,
    cexListed: i.cexListed,
    feeTier,
    feeTierDerived: derived,
    sigmaDaily,
    screens: { lvrBreakeven, flowToxicity, ivRv, funding, liqHeadroom, emissions },
    passCount,
    feeEdgeApr,
    emissionsEdgeApr,
    expectedEdgeAnnual: edge,
    exitsPerYear: exits,
    daysInRange,
    feesPerEpoch,
    churnDragApr,
    netApr,
  };
}

// ── async orchestration ──────────────────────────────────────────────────

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array<R>(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      const item = items[i];
      if (item === undefined) continue;
      out[i] = await fn(item, i);
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return out;
}

async function resolveDetail(
  pool: PoolUniverseItem,
  useSubgraph: boolean,
): Promise<PoolDetail> {
  // GeckoTerminal is authoritative for TVL/volume/price/address (real on-chain
  // reserves). The subgraph is consulted ONLY for the fee tier of the exact
  // pool GeckoTerminal resolved (matched by address) — its TVL is unreliable.
  const gt = await fetchGeckoPoolDetail(pool).catch(() => null);
  const poolAddress = gt?.poolAddress ?? null;

  let feeTier = gt?.feeTier ?? null;
  let usedSubgraph = false;
  if (feeTier === null && useSubgraph && poolAddress && pool.project === 'uniswap-v3') {
    const sgFee = await fetchSubgraphFeeTier(pool, poolAddress).catch(() => null);
    if (sgFee !== null) {
      feeTier = sgFee;
      usedSubgraph = true;
    }
  }

  // 90d average daily volume (smooths the noisy single-day figure) → Gecko 24h
  // → DefiLlama. One extra (throttled) Gecko call per pool.
  const avgVol = poolAddress
    ? await fetchGeckoDailyVolume(gtNetwork(pool.chain), poolAddress, HISTORY_DAYS).catch(() => null)
    : null;

  const source: PoolDetail['source'] = !gt
    ? 'defillama'
    : usedSubgraph
      ? 'gecko+subgraph'
      : 'geckoterminal';
  return {
    poolAddress,
    feeTier,
    tvlUsd: gt?.reserveUsd ?? pool.tvlUsd,
    dailyVolumeUsd: avgVol ?? gt?.volumeUsd24h ?? pool.volumeUsd1d ?? 0,
    midPrice: gt?.baseTokenPriceUsd ?? null,
    source,
  };
}

export interface ScreenPoolsOpts extends UniverseOpts {
  /** Cap the universe scanned (by TVL desc) to respect API rate limits. */
  maxPools?: number;
  /** Query the Uniswap-v3 subgraph proxy for exact fee/TVL. Default true;
   *  degrades to GeckoTerminal automatically when the proxy is unconfigured. */
  useSubgraph?: boolean;
  /** Strategy range half-width for Screen 5. Default 0.10 (±10%). */
  rangeWidth?: number;
  /** Hedge leverage for Screen 5. Default 2. */
  leverage?: number;
  /** Execution friction per redeploy (gas + rebalance slippage + hedge round-
   *  trip), as a fraction of position value. Default 0.0015 (0.15%). */
  redeployCostFraction?: number;
  /** Concurrency for per-pool fetches. Default 4. */
  concurrency?: number;
}

/** Scan the universe and return candidates ranked by expected edge (desc,
 *  nulls last). Network-bound; results are cached 1h per the source clients. */
export async function screenPools(opts: ScreenPoolsOpts = {}): Promise<ScreenedPool[]> {
  const {
    maxPools = 20,
    useSubgraph = true,
    rangeWidth = 0.1,
    leverage = 2,
    redeployCostFraction = 0.0015,
    concurrency = 4,
    ...universeOpts
  } = opts;

  const [universe, cexSymbols] = await Promise.all([
    fetchPoolUniverse(universeOpts),
    fetchBinanceSymbols().catch(() => new Set<string>()),
  ]);
  const scan = universe.slice(0, maxPools);

  // AERO series (emissions decay warning) fetched once, shared across pools.
  const anyAero = scan.some((p) => p.project === 'aerodrome-slipstream');
  const aeroPrices = anyAero
    ? await fetchDailyKlines('AEROUSDT', HISTORY_DAYS)
        .then((k) => k.closes)
        .catch(() => [])
    : [];

  const results = await mapWithConcurrency(scan, concurrency, async (pool) => {
    const detail = await resolveDetail(pool, useSubgraph);
    const base = baseAssetForCex(pool.symbol);
    const cexListed = isCexListed(cexSymbols, base);

    let closes: number[] = [];
    let quoteVolumes: number[] = [];
    let funding: number[] = [];
    if (cexListed) {
      const klines = await fetchDailyKlines(`${base}USDT`, HISTORY_DAYS).catch(() => ({
        closes: [],
        quoteVolumes: [],
      }));
      closes = klines.closes;
      quoteVolumes = klines.quoteVolumes;
      funding = await fetchFundingRates(base, FUNDING_INTERVALS).catch(() => []);
    }

    return runScreens({
      pool,
      detail,
      base,
      cexListed,
      closes,
      quoteVolumes,
      funding,
      aeroPrices,
      rangeWidth,
      leverage,
      redeployCost: redeployCostFraction,
    });
  });

  // Real-TVL floor: DefiLlama's universe pre-filter runs on its own TVL, which
  // is unreliable (it passed a dead $771 pool as $112M). Re-apply the floor on
  // GeckoTerminal's real reserves so dead pools drop out with their phantom edges.
  const tvlFloor = opts.tvlFloorUsd ?? 2_000_000;
  const alive = results.filter((r) => r.detail.tvlUsd >= tvlFloor);

  // Rank by NET APR (after churn) — the real objective, not the gross edge.
  const ranked = alive.sort((a, b) => {
    const na = a.netApr;
    const nb = b.netApr;
    if (na === null && nb === null) return b.passCount - a.passCount;
    if (na === null) return 1;
    if (nb === null) return -1;
    return nb - na;
  });

  // Dedupe DefiLlama entries that resolved to the same on-chain pool (multiple
  // fee-tier entries of a pair collapse when DefiLlama's per-tier TVL is noisy).
  // Keep the highest-ranked occurrence (list is already sorted by edge).
  const seen = new Set<string>();
  return ranked.filter((r) => {
    const key = r.detail.poolAddress?.toLowerCase() ?? r.pool.poolId;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
